/**
 * Feed Post API port.
 *
 * ACT-INDEEP-FRONTEND-FEED-COMPOSER01-CORRECTION01: Fail-closed stub.
 * ACT-INDEEP-FRONTEND-FEED-COMPOSER01-CORRECTION02: FeedPostError removed (dead code).
 * ACT-INDEEP-PRODUCTION-FEED-FIGMA-REPAIR01 (Phase D): added
 *   `listFeedPosts` port bound to GET /api/v1/feed/.
 * ACT-INDEEP-PRODUCTION-FEED-COMPOSER-API-WIRING01: wired
 *   `createPostAdapter` to POST /api/v1/feed/posts.
 * ACT-INDEEP-PRODUCTION-FEED-CSRF-SESSION-CONTRACT01: error classification
 *   split so CSRF/security failures do NOT collapse onto `forbidden`.
 *
 * Security posture:
 * - No account_id or actor identity fields in request payload
 * - Backend derives actor identity from authenticated session
 * - Post content NOT logged in error messages
 *
 * Wire authority (bound mechanically at Phase A of this ACT):
 *   - endpoint:    POST /api/v1/feed/posts
 *   - request:     api/openapi/schemas/feed.yaml#/CreatePostRequest
 *                  { content: string (1..500) REQUIRED,
 *                    is_anonymous: boolean OPTIONAL default false }
 *   - success:     201 Created with PostResponse
 *   - errors:      400 invalid, 401 unauth,
 *                  403 csrf_token_required / csrf_token_invalid / "csrf token required"
 *                       → session_required (security/session failure)
 *                  403 active_profile_required / unclassified
 *                       → forbidden (business authorization gate)
 *                  5xx/network → unavailable
 *   - CSRF:        X-CSRF-Token header required by auth.RequireCSRF
 *                  middleware (mirrors chat.ts / skills.ts / profilePhotos.ts).
 *                  ensureCSRFToken() always issues a fresh
 *                  `GET /api/auth/csrf` per unsafe mutation; the
 *                  backend's CSRFStore is single-use, so a persistent
 *                  client cache would hand the next mutation a
 *                  consumed token and surface as `csrf token invalid`.
 */

import { ensureCSRFToken, CSRF_HEADER_NAME } from './auth';

// =============================================================================
// Types
// =============================================================================

/**
 * Request payload for creating a feed post.
 *
 * Security: content is the only user-supplied field.
 * Actor identity (account_id) is server-derived from the authenticated session.
 */
export interface CreatePostCommand {
  /** Post text content (1–500 characters) */
  readonly content: string;
  /** Whether to post anonymously */
  readonly anonymous: boolean;
}

/**
 * Discriminated result vocabulary for post creation.
 *
 * CORRECTION01: Replaces boolean ok field with closed union.
 * The frontend may adapt this when the real HTTP contract becomes authoritative.
 *
 * ACT-INDEEP-PRODUCTION-FEED-CSRF-SESSION-CONTRACT01:
 *   `session_required` is the explicit CSRF/security failure variant.
 *   `forbidden` is reserved for the business authorization gate
 *   (e.g. active_profile_required). The two MUST NOT collapse —
 *   see effects.ts for the UI mapping invariant.
 */
export type CreatePostResult =
  /** Post created successfully */
  | { readonly kind: 'created'; readonly postId: string }
  /** Validation failed on a specific field */
  | { readonly kind: 'validation_failed'; readonly field: 'content' | 'anonymous'; readonly code: string }
  /** User is not authenticated */
  | { readonly kind: 'unauthenticated' }
  /**
   * CSRF/security/session failure: the request reached the backend
   * but was rejected because the session cookie / CSRF token pair
   * could not be validated. Distinct from `forbidden` because the
   * underlying cause is the security contract, not the user's
   * business eligibility to post.
   *
   * UI MUST NOT render this as "У вас нет прав для публикации."
   * (false forbidden copy). It MUST render a recoverable
   * session/security message.
   */
  | { readonly kind: 'session_required' }
  /**
   * Business authorization failure: the user is authenticated, the
   * CSRF check passed, but the user lacks the business eligibility
   * to post (e.g. inactive profile). Reserved for the active-profile
   * gate. UI renders "Публикация недоступна для текущего статуса
   * профиля." (legacy: "У вас нет прав для публикации.").
   */
  | { readonly kind: 'forbidden' }
  /** Conflict: duplicate post detected */
  | { readonly kind: 'conflict' }
  /** Service unavailable: backend endpoint not yet implemented */
  | { readonly kind: 'unavailable' }
  /** Unexpected failure: catch-all for unknown errors */
  | { readonly kind: 'unexpected_failure' };

/**
 * The port interface for post creation.
 * Allows injection of real or test adapters.
 *
 * CORRECTION01: This interface enables dependency injection.
 * The concrete stub returns 'unavailable' until the real backend is ready.
 */
export interface CreatePostPort {
  create(command: CreatePostCommand, signal: AbortSignal): Promise<CreatePostResult>;
}

// =============================================================================
// Production adapter — wired to POST /api/v1/feed/posts
// =============================================================================
//
// ACT-INDEEP-PRODUCTION-FEED-COMPOSER-API-WIRING01:
// The previous stub returned { kind: 'unavailable' } unconditionally,
// which mirrored "endpoint not yet available" but produced zero
// network traffic. The backend now exposes POST /api/v1/feed/posts
// (internal/feed/http/post_handlers.go:75 CreatePost), so the
// composer submit path can be exercised end-to-end.
//
// Wire contract (api/openapi/paths/feed.yaml:49-88):
//
//   POST /api/v1/feed/posts
//     body: application/json { content, is_anonymous }
//     201  → PostResponse (authoritative id at .id)
//     400  → validation_failed (field: 'content')
//     401  → unauthenticated
//     403  → forbidden (active profile required)
//     5xx  → unavailable
//
// Error mapping is closed-set so the UI message is authoritative and
// raw exception text never leaks (R9).

/**
 * Network base path for the create-post endpoint.
 *
 * Per api/openapi/paths/feed.yaml:49-88, the route is
 * `/api/v1/feed/posts` (no trailing slash). Trailing-slash variants
 * would mismatch the chi router and surface as 404 → mapped to
 * `unavailable`, so the path is hard-pinned here.
 */
const FEED_CREATE_PATH = '/api/v1/feed/posts';

/**
 * Validate that a 201 response body matches the OpenAPI PostResponse
 * contract (api/openapi/schemas/feed.yaml:38-87).
 *
 * Fail-closed: a malformed 201 body MUST NOT surface as `created`
 * (which would let a backend bug render an unbacked post on the
 * client). Mirrors the list adapter's `asFeedPage` validator pattern
 * (CORRECTION08 P0-01).
 *
 * The returned `id` MUST be a non-empty string; everything else is
 * required-shape but we don't project the success payload to the
 * caller — the composer only needs the post id; the feed list
 * refreshes from GET /api/v1/feed/.
 */
function asCreatePostResponseId(body: unknown): string | null {
  if (typeof body !== 'object' || body === null) return null;
  const v = body as Record<string, unknown>;
  if (typeof v.id !== 'string' || v.id.length === 0) return null;
  if (typeof v.content !== 'string') return null;
  if (typeof v.created_at !== 'string') return null;
  if (typeof v.updated_at !== 'string') return null;
  if (typeof v.last_activity_at !== 'string') return null;
  if (typeof v.has_comments !== 'boolean') return null;
  const author = v.author;
  if (typeof author !== 'object' || author === null) return null;
  const a = author as Record<string, unknown>;
  if (typeof a.display_name !== 'string') return null;
  if (typeof a.is_anonymous !== 'boolean') return null;
  const like_summary = v.like_summary;
  if (typeof like_summary !== 'object' || like_summary === null) return null;
  const ls = like_summary as Record<string, unknown>;
  if (typeof ls.count !== 'number') return null;
  if (typeof ls.viewer_has_liked !== 'boolean') return null;
  return v.id;
}

/**
 * Production adapter for post creation.
 *
 * ACT-INDEEP-PRODUCTION-FEED-COMPOSER-API-WIRING01: replaces the
 * CORRECTION01 fail-closed stub. Calls POST /api/v1/feed/posts with
 * the exact OpenAPI request shape and maps the closed-set of
 * responses back to the existing `CreatePostResult` union.
 *
 * Security:
 * - No actor identity fields in the request payload (backend
 *   derives them from the session; post_handlers.go:104-108).
 * - Post content is NEVER included in error messages — only the
 *   mapped safe message reaches the UI (effects.ts:30-37).
 * - Raw exception text never leaks (effects.ts:runEffect wraps in
 *   `mapUnknownErrorToSafe`).
 */
export const createPostAdapter: CreatePostPort = {
  async create(
    command: CreatePostCommand,
    signal: AbortSignal,
  ): Promise<CreatePostResult> {
    // ACT-INDEEP-PRODUCTION-FEED-CSRF-SESSION-CONTRACT01 (CORRECTION01):
    // Every unsafe mutation MUST acquire a fresh single-use CSRF
    // token. The backend's CSRFStore deletes a token after a
    // successful ValidateToken call (see internal/auth/csrf.go:177-180),
    // so a persistent client cache would hand the next mutation a
    // consumed token and surface as `csrf token invalid`.
    //
    // ensureCSRFToken() always issues `GET /api/auth/csrf` and
    // returns the freshly issued token (or undefined). We use that
    // return value directly — we never fall back to the cookie value,
    // because the cookie MAY carry a token that has already been
    // consumed by a prior mutation in another tab.
    //
    // Production evidence that justifies the defensive GET:
    // `document.cookie === ""` on https://indeep.info/feed/compose
    // even though the session cookie is present.
    const csrfToken = await ensureCSRFToken();
    const requestBody = JSON.stringify({
      content: command.content,
      is_anonymous: command.anonymous,
    });

    let response: Response;
    try {
      response = await fetch(FEED_CREATE_PATH, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { [CSRF_HEADER_NAME]: csrfToken } : {}),
        },
        credentials: 'include',
        body: requestBody,
        signal,
      });
    } catch {
      // Fetch threw (network failure, AbortError on unmount, etc.).
      // Surfaced as a closed-set variant; the raw error string MUST
      // NOT propagate to the UI (effects.ts:runEffect's catch wraps
      // the result through `mapUnknownErrorToSafe`).
      return { kind: 'unavailable' };
    }

    // Status-class mapping is bounded to the OpenAPI-declared
    // statuses for this endpoint (api/openapi/paths/feed.yaml:64-88).
    if (response.status === 401) {
      return { kind: 'unauthenticated' };
    }
    if (response.status === 403) {
      // ACT-INDEEP-PRODUCTION-FEED-CSRF-SESSION-CONTRACT01:
      // 403 + csrf_token_required/invalid is a SECURITY/SESSION
      // failure (RequireCSRF rejected the request), NOT a business
      // authorization failure. Distinguish by parsing the JSON
      // body; if the body's `error` field names a CSRF failure,
      // route to `session_required`. Otherwise (or when the body
      // does not identify itself), preserve the legacy `forbidden`
      // mapping so the active-profile business gate is not weakened.
      let csrfLike = false;
      try {
        const body = await response.json();
        if (
          typeof body === 'object' &&
          body !== null &&
          typeof (body as Record<string, unknown>).error === 'string'
        ) {
          const code = ((body as Record<string, unknown>).error as string)
            .toLowerCase()
            .trim();
          // Accept BOTH the canonical snake_case form
          // (`csrf_token_required` / `csrf_token_invalid`) AND the
          // legacy human-prose form (`csrf token required` /
          // `csrf token invalid`) that the backend currently emits.
          if (
            code === 'csrf_token_required' ||
            code === 'csrf_token_invalid' ||
            code === 'csrf token required' ||
            code === 'csrf token invalid'
          ) {
            csrfLike = true;
          }
        }
      } catch {
        // Non-JSON body — preserve the legacy 403 → forbidden mapping.
      }
      if (csrfLike) {
        return { kind: 'session_required' };
      }
      return { kind: 'forbidden' };
    }
    if (response.status === 400) {
      // Backend may include an `error` string; we expose it as the
      // `code` on `validation_failed` for caller-side diagnostics
      // (effects.ts never renders it directly; UI uses the safe
      // SAFE_ERROR_MESSAGES.validation_failed copy).
      let code = 'invalid_request';
      try {
        const body = await response.json();
        if (
          typeof body === 'object' &&
          body !== null &&
          typeof (body as Record<string, unknown>).error === 'string'
        ) {
          code = (body as Record<string, unknown>).error as string;
        }
      } catch {
        // Body was not JSON; keep the generic code.
      }
      return { kind: 'validation_failed', field: 'content', code };
    }
    if (response.status === 201) {
      let body: unknown;
      try {
        body = await response.json();
      } catch {
        // 201 with non-JSON body is a server contract violation;
        // surface as unexpected_failure so the UI stays in a stable
        // non-success state instead of falsely navigating.
        return { kind: 'unexpected_failure' };
      }
      const postId = asCreatePostResponseId(body);
      if (postId === null) {
        return { kind: 'unexpected_failure' };
      }
      return { kind: 'created', postId };
    }

    // All other statuses (including 5xx and any undeclared 4xx)
    // collapse to the safe "Сервис публикации временно недоступен"
    // message via the existing `unavailable` variant.
    return { kind: 'unavailable' };
  },
};

// =============================================================================
// Character limit
// =============================================================================

/**
 * Get the canonical character limit for post content.
 *
 * IMPORTANT: This value MUST be verified against the backend.
 * The backend may use UTF-8 bytes, Unicode code points, or grapheme clusters.
 * JavaScript string.length does NOT always match backend semantics.
 *
 * TODO: Fetch this value from the backend or confirm equivalence.
 */
export const MAX_CONTENT_LENGTH = 500;

// =============================================================================
// List endpoint — GET /api/v1/feed/
// =============================================================================
//
// ACT-INDEEP-PRODUCTION-FEED-FIGMA-REPAIR01 (Phase D):
// Wire authority for the Figma 38:904 "Без фильтров" Feed surface
// lives here. Per the expert architectural suggestion (Phase D plan),
// network authority is kept OUT of the React view: FeedList consumes
// `listFeedPosts` and is responsible only for async UI state +
// projection.
//
// Wire contract (api/openapi/paths/feed.yaml + schemas/feed.yaml):
//
//   GET /api/v1/feed/?limit=20&offset=0
//     → 200 application/json
//       { posts: PostResponse[], limit, offset, has_more }
//
// Anti-Paper-RED rules bound at this layer:
//
//   R1: PostResponse.author carries NO `age` field. The FeedAuthor
//       interface below has NO `age?: number` member. The strongest
//       R1 witness lives at the wire boundary (FeedList.test.tsx).
//   R2: GET is the EFFECTIVE method (the fetch call below omits
//       method and relies on the Fetch API default). The Playwright
//       spec asserts `init?.method ?? 'GET' === 'GET'` to avoid
//       pinning a particular JS-call encoding.
//   R3: AuthorDisplay projection is driven by `is_anonymous` boolean,
//       never by string substitution. FeedAuthor intentionally
//       carries `display_name` (the wire value, even when hostile);
//       the consumer (FeedPostCard) is responsible for projecting
//       "Аноним" when `is_anonymous === true`.

/**
 * How authorship is presented to the viewer (per OpenAPI
 * `AuthorPresentation`).
 *
 * The wire's `display_name` is preserved verbatim here so a hostile
 * display_name flows through. Anonymity is driven by the boolean
 * `is_anonymous` flag in the consumer (FeedPostCard).
 *
 * No `age` field. R1 wire-boundary witness.
 */
export interface FeedAuthor {
  readonly display_name: string;
  readonly is_anonymous: boolean;
}

/**
 * Like summary (per OpenAPI `LikeSummary`).
 *
 * `count` is bound (Figma 38:904 shows the count).
 * `viewer_has_liked` is AVAILABLE_ON_WIRE but the LIKED_VISUAL_STATE
 * is DEFERRED_NO_FIGMA_AUTHORITY — Figma 38:904 shows only the
 * unliked state, so binding a fill-state contract here would invent
 * design. Do NOT extend this interface to drive fill-state rendering
 * until the LIKED_VISUAL_STATE gets a Figma authority.
 */
export interface FeedLikeSummary {
  readonly count: number;
  readonly viewer_has_liked: boolean;
}

/**
 * A single feed post as returned by GET /api/v1/feed/.
 *
 * Wire schema: api/openapi/schemas/feed.yaml (PostResponse).
 *
 * R1: no `author.age`. The FeedPostCard prop surface intentionally
 *     does not accept authorAge; the card cannot mis-render an age
 *     it never receives.
 */
export interface FeedPost {
  readonly id: string;
  readonly content: string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly last_activity_at: string;
  readonly author: FeedAuthor;
  readonly like_summary: FeedLikeSummary;
  /** Optional in the wire schema. Only present when the backend has it. */
  readonly comment_count?: number;
  readonly has_comments: boolean;
}

/**
 * Paginated feed page (per OpenAPI `FeedPage`).
 */
export interface FeedPage {
  readonly posts: readonly FeedPost[];
  readonly limit: number;
  readonly offset: number;
  readonly has_more: boolean;
}

/**
 * Discriminated result vocabulary for the list endpoint.
 *
 * Mirrors the CreatePostResult pattern: closed union so callers must
 * handle each shape explicitly. The error variants use closed-set
 * copy (no raw exception text) to keep the UI message authoritative
 * and prevent accidental stack-trace leaks.
 */
export type ListFeedPostsResult =
  /** Successful fetch, may be empty. */
  | { readonly kind: 'ok'; readonly page: FeedPage }
  /** Network failure (fetch threw, non-2xx response, or JSON parse error). */
  | { readonly kind: 'network_error' }
  /** Backend explicitly returned 401. Caller should treat as unauthenticated. */
  | { readonly kind: 'unauthenticated' };

/**
 * Options accepted by the list endpoint.
 *
 * `limit` is bounded 1..100 (mirrors the OpenAPI schema minimum/maximum).
 * Default: 20 (matches the OpenAPI default).
 */
export interface ListFeedPostsOptions {
  readonly limit?: number;
  readonly offset?: number;
  /** AbortSignal — feed controllers should abort on unmount. */
  readonly signal?: AbortSignal;
}

/**
 * The port interface for the feed list endpoint.
 *
 * Allows injection of real or test adapters.
 */
export interface ListFeedPostsPort {
  list(options: ListFeedPostsOptions): Promise<ListFeedPostsResult>;
}

// =============================================================================
// Production adapter — wired to GET /api/v1/feed/
// =============================================================================

/**
 * Network base path for the feed surface.
 *
 * Per api/openapi/paths/feed.yaml line 8, the list endpoint is
 * `/api/v1/feed/` (with trailing slash). The Playwright spec
 * matches both `/api/v1/feed/` and `/api/v1/feed/?limit=...` —
 * see the `/\/api\/v1\/feed\/(?:\?.*)?$/` regex.
 */
const FEED_LIST_BASE = '/api/v1/feed';

/**
 * Strict integer + range validator for the `limit` query parameter.
 *
 * CORRECTION09 (P1-OPEN PAGINATION_RUNTIME_CONTRACT):
 * Per OpenAPI `limit` is `integer`, `minimum: 1`, `maximum: 100`,
 * `default: 20`. We require an INTEGER — not just a finite number
 * — because fractional values are never semantically meaningful
 * for a page size and would mask caller bugs. Returning a boolean
 * keeps the helper pure (no exception control flow at the call
 * site) and lets the adapter throw a descriptive `RangeError` from
 * a single, named location.
 */
export function isValidLimit(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= 100
  );
}

/**
 * Strict integer + range validator for the `offset` query parameter.
 *
 * CORRECTION09 (P1-OPEN PAGINATION_RUNTIME_CONTRACT):
 * Per OpenAPI `offset` is `integer`, `minimum: 0`, `default: 0`.
 * Same integer rule as `limit` — fractional offsets have no defined
 * semantics and would round unpredictably across servers.
 */
export function isValidOffset(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 0
  );
}

/**
 * Fail-fast request-side validation. Throws RangeError BEFORE
 * issuing any network request when caller-supplied pagination
 * options are out of contract. This prevents a UI bug emitting a
 * fractional or out-of-range `limit` / `offset` from being silently
 * coerced (or dropped) by `buildFeedListUrl` and reaching the server
 * behind a different URL than the caller intended.
 *
 * `undefined` is allowed (the adapter applies OpenAPI defaults
 * downstream). Only explicit bad values throw.
 */
export function assertListOptionsOrThrow(options: ListFeedPostsOptions): void {
  if (options.limit !== undefined && !isValidLimit(options.limit)) {
    throw new RangeError(
      `listFeedPostsAdapter.list: invalid 'limit'=${String(options.limit)}; must be an integer in [1, 100].`,
    );
  }
  if (options.offset !== undefined && !isValidOffset(options.offset)) {
    throw new RangeError(
      `listFeedPostsAdapter.list: invalid 'offset'=${String(options.offset)}; must be an integer >= 0.`,
    );
  }
}

/**
 * Build the canonical URL for the list endpoint, encoding options as
 * query parameters. Avoids hand-concatenating user input.
 *
 * CORRECTION09: now requires INTEGER values (not just finite
 * numbers) for both `limit` and `offset`. Silently dropping an
 * invalid caller option would hide upstream bugs (e.g. a UI bug
 * emitting `limit: 1.5`); instead the adapter throws a
 * `RangeError` from `listFeedPostsAdapter.list()` BEFORE issuing
 * any network request. Response-side validation in `asFeedPage`
 * uses the same `isValidLimit` / `isValidOffset` predicates, so the
 * two halves of the contract cannot drift.
 */
function buildFeedListUrl(options: ListFeedPostsOptions): string {
  const params = new URLSearchParams();
  // Per OpenAPI: limit default 20, min 1, max 100, integer only.
  const limit = options.limit ?? 20;
  if (isValidLimit(limit)) {
    params.set('limit', String(limit));
  }
  if (isValidOffset(options.offset)) {
    params.set('offset', String(options.offset));
  }
  const qs = params.toString();
  return qs.length > 0 ? `${FEED_LIST_BASE}/?${qs}` : `${FEED_LIST_BASE}/`;
}

/**
 * Validate a parsed JSON object as a FeedPage.
 *
 * CORRECTION08 (P0-01 RESPONSE_SHAPE_VALIDATION):
 * Defense-in-depth at the wire boundary. The adapter narrows the
 * response shape — including every nested PostResponse — before
 * handing it to the UI. A malformed payload (missing required
 * field, wrong type, negative count) is surfaced as
 * `network_error`, never as a crash inside React during projection.
 *
 * The previous implementation only validated the envelope
 * (posts:Array, limit:number, offset:number, has_more:boolean) and
 * trusted individual posts via `as readonly FeedPost[]`. A
 * syntactically valid envelope carrying a malformed post would
 * pass `asFeedPage()` and then throw inside FeedList when it
 * dereferenced `post.author.display_name`. The browser F-5/F-6
 * matrix would still pass — happy-path fixtures are well-formed —
 * so this gap was invisible behind a passing browser witness.
 */
function asFeedPage(value: unknown): FeedPage | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (!Array.isArray(v.posts)) return null;
  // CORRECTION09: integer + 1..100 range enforced on the wire envelope.
  if (!isValidLimit(v.limit)) return null;
  // CORRECTION09: integer + >=0 enforced on the wire envelope.
  if (!isValidOffset(v.offset)) return null;
  if (typeof v.has_more !== 'boolean') return null;

  // Per-element narrowing is STRICT: any malformed post fails the
  // whole response. The alternative (per-element permissive +
  // runtime defensive checks at every dereference) is brittle and
  // easy to drift on; centralizing at the boundary is cheaper and
  // gives a stable "network_error" semantic the UI can render.
  const posts: FeedPost[] = [];
  for (const raw of v.posts) {
    const post = asFeedPost(raw);
    if (post === null) return null;
    posts.push(post);
  }

  return {
    posts,
    limit: v.limit,
    offset: v.offset,
    has_more: v.has_more,
  };
}

/**
 * Validate a parsed JSON object as a FeedAuthor (per OpenAPI
 * AuthorPresentation).
 *
 * Required: display_name (string), is_anonymous (boolean).
 * The wire MUST NOT carry an age field on a feed post
 * (REQ-FEED-RELEASE2-ANONYMITY-001, R1 in this module). If a future
 * response includes an `age` field on the author it is intentionally
 * IGNORED / NOT PROJECTED onto the `FeedAuthor` value returned to
 * the UI — this function builds a fresh object literal that binds
 * ONLY `display_name` and `is_anonymous`. It does NOT pass through
 * the source value. (CORRECTION09 P2-DOC.)
 */
function asFeedAuthor(value: unknown): FeedAuthor | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (typeof v.display_name !== 'string') return null;
  if (typeof v.is_anonymous !== 'boolean') return null;
  return {
    display_name: v.display_name,
    is_anonymous: v.is_anonymous,
  };
}

/**
 * Validate a parsed JSON object as a FeedLikeSummary (per OpenAPI
 * LikeSummary).
 *
 * Required: count (finite non-negative integer), viewer_has_liked
 * (boolean). The count is bound for display-only rendering (the
 * fill state is DEFERRED_NO_FIGMA_AUTHORITY per the WAL).
 */
function asFeedLikeSummary(value: unknown): FeedLikeSummary | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (
    typeof v.count !== 'number' ||
    !Number.isFinite(v.count) ||
    !Number.isInteger(v.count) ||
    v.count < 0
  ) {
    return null;
  }
  if (typeof v.viewer_has_liked !== 'boolean') return null;
  return {
    count: v.count,
    viewer_has_liked: v.viewer_has_liked,
  };
}

/**
 * Validate a parsed JSON object as a FeedPost (per OpenAPI
 * PostResponse).
 *
 * Required: id (string), content (string), created_at (string),
 * updated_at (string), last_activity_at (string), author
 * (object), like_summary (object), has_comments (boolean).
 *
 * Optional: comment_count (finite non-negative integer if present).
 *
 * Nested author and like_summary are validated recursively.
 */
function asFeedPost(value: unknown): FeedPost | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (typeof v.id !== 'string') return null;
  if (typeof v.content !== 'string') return null;
  if (typeof v.created_at !== 'string') return null;
  if (typeof v.updated_at !== 'string') return null;
  if (typeof v.last_activity_at !== 'string') return null;
  if (typeof v.has_comments !== 'boolean') return null;

  const author = asFeedAuthor(v.author);
  if (author === null) return null;

  const like_summary = asFeedLikeSummary(v.like_summary);
  if (like_summary === null) return null;

  // comment_count is OPTIONAL in the OpenAPI schema. When present,
  // it MUST be a finite non-negative integer.
  let comment_count: number | undefined;
  if (v.comment_count !== undefined) {
    if (
      typeof v.comment_count !== 'number' ||
      !Number.isFinite(v.comment_count) ||
      !Number.isInteger(v.comment_count) ||
      v.comment_count < 0
    ) {
      return null;
    }
    comment_count = v.comment_count;
  }

  return {
    id: v.id,
    content: v.content,
    created_at: v.created_at,
    updated_at: v.updated_at,
    last_activity_at: v.last_activity_at,
    author,
    like_summary,
    ...(comment_count !== undefined ? { comment_count } : {}),
    has_comments: v.has_comments,
  };
}

/**
 * Production adapter for GET /api/v1/feed/.
 *
 * Uses the global `fetch` (injected in tests via
 * `globalThis.fetch = ...`). The effective request method is GET (the
 * Fetch API default when no `method` is provided). The Playwright spec
 * asserts the EFFECTIVE method via `init?.method ?? 'GET'`, which is
 * consistent with this shape.
 */
export const listFeedPostsAdapter: ListFeedPostsPort = {
  async list(options: ListFeedPostsOptions): Promise<ListFeedPostsResult> {
    // CORRECTION09 (P1-OPEN PAGINATION_RUNTIME_CONTRACT):
    // Fail-fast request-side validation BEFORE any network IO so a
    // UI bug emitting a fractional or out-of-range `limit` / `offset`
    // is caught at the seam with a descriptive RangeError, instead
    // of being silently coerced by `buildFeedListUrl`. We deliberately
    // do NOT collapse this into a try/catch inside the fetch — that
    // would re-shape programmer errors as user-facing `network_error`.
    assertListOptionsOrThrow(options);

    const url = buildFeedListUrl(options);
    let response: Response;
    try {
      response = await fetch(url, {
        // Method omitted on purpose: relies on the Fetch API default
        // (GET). The R2 spec assertion is `init?.method ?? 'GET'`,
        // so a future change that explicitly passes method: 'GET' is
        // also valid.
        credentials: 'include',
        signal: options.signal,
      });
    } catch {
      // Fetch threw (network failure, AbortError on unmount, etc.).
      // Surfaced as a closed-set variant; the raw error string MUST
      // NOT propagate to the UI (FeedList renders a stable message).
      return { kind: 'network_error' };
    }

    if (response.status === 401) {
      return { kind: 'unauthenticated' };
    }

    if (!response.ok) {
      return { kind: 'network_error' };
    }

    let body: unknown;
    try {
      body = await response.json();
    } catch {
      return { kind: 'network_error' };
    }

    const page = asFeedPage(body);
    if (!page) {
      return { kind: 'network_error' };
    }

    return { kind: 'ok', page };
  },
};
